// External dependencies
import { type FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { _isNotBlank } from '@/littledash';

// Internal components
import ReasonForChange from '@/components/Shared/Latest/ReasonForChange';

interface ResumeStudyFormData {
  reason_for_change?: string;
}

interface ResumeStudyProps {
  closeModal: () => void;
  onClick: (reasonForChange?: string) => void;
  testId?: string;
}

/**
 * Confirmation modal for resuming a completed or cancelled study.
 * When the reason_for_change feature is enabled, collects a required reason (3–255 chars)
 * via the shared ReasonForChange field before calling onClick.
 */
const ResumeStudy: FC<ResumeStudyProps> = ({ closeModal, onClick, testId = 'resume-study' }) => {
  const formMethods = useForm<ResumeStudyFormData>({
    mode: 'onChange',
    defaultValues: { reason_for_change: '' },
  });
  const { handleSubmit, formState } = formMethods;

  const onSubmit = (form: ResumeStudyFormData): void => {
    const reason = form.reason_for_change?.trim();
    onClick(_isNotBlank(reason) ? reason : undefined);
  };

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="ui__narrow-modal"
        data-test-component="ResumeStudy"
        data-testid={testId}
      >
        <div className="pb3 tc">
          <h3 className="normal f4 pb2">Resume study</h3>
          <p className="f6 pb2">
            Resume the study from where it was left off. All invited participants will be notified by email.
          </p>
        </div>
        <div className="pb3 tl">
          <ReasonForChange />
        </div>
        <div>
          <button
            type="submit"
            className="button w-100"
            data-testid="resume-study__submit"
            disabled={formState.isSubmitting}
          >
            Resume study
          </button>
          <button
            type="button"
            className="button plain db w-100 mt2"
            onClick={closeModal}
            data-testid="resume-study__cancel"
          >
            Cancel
          </button>
        </div>
      </form>
    </FormProvider>
  );
};

export default ResumeStudy;
